import { PolicyGroups } from 'shared/utils/policy-controller';
import { BranchOfficeRole, CompanyRole } from '@types';

/**
 * This exists only because the routing system we have doesn't work well with different layouts provided to different routes.
 * TODO sort this out and refactor routing in a way:
 *  - we have routes hierarchy in place
 *  - we can provide different layouts to different routes
 */
const paths = {
  Unauthorized: {
    path: 'unauthorized',
    exact: true,
  },
  Dashboard: {
    path: 'dashboard',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.SPECTATOR, BranchOfficeRole.PLANNER],
  },
  FlexPools: {
    path: 'planning/flexPools',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.SPECTATOR, BranchOfficeRole.PLANNER],
    queryParams: {
      FLEXPOOL_ID: 'flexPoolID',
      TAB: 'tab',
      WORKERS_POOL_ID: 'workersPoolId',
    },
  },
  FlexPoolCreate: {
    path: 'planning/flexPools/create',
    fallbackPath: 'planning/flexPools/',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.PLANNER, BranchOfficeRole.MANAGER],
  },
  FlexPoolExternalCreate: {
    path: 'planning/flexPools/external/create',
    fallbackPath: 'planning/flexPools/',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.PLANNER, BranchOfficeRole.MANAGER],
  },
  WorkersPoolEdit: {
    path: 'planning/flexPools/external/edit',
    fallbackPath: 'planning/flexPools/',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.PLANNER, BranchOfficeRole.MANAGER],
    queryParams: {
      WORKERS_POOL_ID: 'workersPoolId',
    },
  },
  FlexPoolEdit: {
    path: 'planning/flexPools/edit',
    fallbackPath: 'planning/flexPools/',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.PLANNER, BranchOfficeRole.MANAGER],
    queryParams: {
      FLEXPOOL_ID: 'flexPoolID',
    },
  },
  WorkTemplates: {
    path: 'planning/work-templates',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  WorkTemplatesEdit: {
    path: 'planning/work-templates/:id/edit',
    fallbackPath: 'planning/work-templates',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  WorkTemplatesCreate: {
    path: 'planning/work-templates/create',
    fallbackPath: 'planning/work-templates',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  WorkTemplatesDuplicate: {
    path: 'planning/work-templates/:id/duplicate',
    fallbackPath: 'planning/work-templates',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  WorkTemplatesDetail: {
    path: 'planning/work-templates/:id',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.PLANNER],
  },
  ShiftPlanning: {
    path: 'planning/shifts/*',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.SPECTATOR, BranchOfficeRole.PLANNER],
    fallbackPath: 'unauthorized',
  },
  Workforce: {
    path: 'management/workforce',
    fallbackPath: 'unauthorized',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.PLANNER],
  },
  CollegialLendingCustomers: {
    path: 'planning/collegial-lending/customers',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.SPECTATOR, BranchOfficeRole.PLANNER],
    fallbackPath: 'unauthorized',
  },
  CollegialLendingCustomersCreate: {
    path: 'planning/collegial-lending/customers/create',
    exact: true,
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
    fallbackPath: 'unauthorized',
  },
  CollegialLendingCustomerDetail: {
    path: 'planning/collegial-lending/customers/:id',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
    fallbackPath: 'planning/collegial-lending/customers',
  },
  CollegialLendingSuppliers: {
    path: 'planning/collegial-lending/suppliers',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.SPECTATOR, BranchOfficeRole.PLANNER],
    fallbackPath: 'unauthorized',
  },
  ServiceAgreementDetail: {
    path: 'planning/collegial-lending/service-agreements/:id',
    fallbackPath: 'planning/collegial-lending/customers',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.SPECTATOR, BranchOfficeRole.PLANNER],
  },
  EmployeeDetail: {
    path: 'management/workforce/:id',
    fallbackPath: 'management/workforce',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.PLANNER],
  },
  EmployeeCreate: {
    path: 'management/workforce/create',
    fallbackPath: 'management/workforce/',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.MANAGER],
  },
  EmployeeUpdate: {
    path: 'management/workforce/:id/edit',
    fallbackPath: 'management/workforce/',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.MANAGER],
  },
  EmployeeCertificateAdd: {
    path: 'management/workforce/:id/certificates/add',
    fallbackPath: 'management/workforce/',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: [BranchOfficeRole.MANAGER],
  },
  Company: {
    path: 'management/company',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  CompanyEdit: {
    path: 'management/company/edit',
    exact: true,
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  CompanyContractorRatesEdit: {
    path: 'management/company/contractor-rates/edit',
    exact: true,
    fallbackPath: 'management/company',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  BranchOffices: {
    path: 'management/branch-offices',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  BranchOfficesEdit: {
    path: 'management/branch-offices/:id/edit',
    fallbackPath: 'management/branch-offices',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  BranchOfficesDuplicate: {
    path: 'management/branch-offices/:id/duplicate',
    fallbackPath: 'management/branch-offices',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  BranchOfficesCreate: {
    path: 'management/branch-offices/create',
    fallbackPath: 'management/branch-offices',
    exact: true,
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  BranchOfficesDetail: {
    path: 'management/branch-offices/:id',
    fallbackPath: 'management/branch-offices',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  Users: {
    exact: true,
    path: 'management/users',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  UsersEdit: {
    path: 'management/users/:id/edit',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  UsersCreate: {
    exact: true,
    path: 'management/users/create',
    fallbackPath: 'management/users',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  UsersDetail: {
    path: 'management/users/:id',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  BillingEntitiesCreate: {
    path: 'management/company/billing-entities/create',
    fallbackPath: 'management/company',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  BillingEntitiesDetail: {
    path: 'management/company/billing-entities/:id',
    fallbackPath: 'management/company',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  BillingEntitiesDuplicate: {
    path: 'management/company/billing-entities/:id/duplicate',
    fallbackPath: 'management/company',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  BillingEntitiesEdit: {
    path: 'management/company/billing-entities/:id/edit',
    fallbackPath: 'management/company',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  CheckoutsInbox: {
    path: 'checkout/inbox',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.PLANNER,
    queryParams: {
      FILTER_TYPE: 'filterByPrio',
      DEPARTMENT_IDS: 'departmentIds',
      FLEXWORKER_IDS: 'flexWorkerIds',
      BRANCH_OFFICE_ID: 'branchOfficeId',
      IS_FREELANCE: 'isFreelance',
      IS_TEMPWORK: 'isTempWork',
      IS_EMPLOYEE: 'isEmployee',
      FROM_DATE: 'fromDate',
      TO_DATE: 'toDate',
      JOB_IDS: 'jobIds',
      SORT_KEY: 'sort',
      SORT_ORDER: 'order',
    },
  },
  CheckoutsArchive: {
    path: 'checkout/archive',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.PLANNER,
  },
  Projects: {
    path: 'planning/projects',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.PLANNER,
  },
  ProjectsEdit: {
    path: 'planning/projects/:id/edit',
    fallbackPath: 'planning/projects',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.PLANNER,
  },
  ProjectsCreate: {
    path: 'planning/projects/create',
    fallbackPath: 'planning/projects',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.PLANNER,
  },
  ProjectsDuplicate: {
    path: 'planning/projects/:id/duplicate',
    fallbackPath: 'planning/projects',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.PLANNER,
  },
  Departments: {
    path: 'management/departments',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  DepartmentDetail: {
    path: 'management/departments/:id',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  DepartmentEdit: {
    path: 'management/departments/:id/edit',
    fallbackPath: 'management/departments',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  DepartmentCreate: {
    path: 'management/departments/create',
    fallbackPath: 'management/departments',
    exact: true,
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  DepartmentDuplicate: {
    path: 'management/departments/:id/duplicate',
    fallbackPath: 'management/departments',
    policyGroup: PolicyGroups.COMPANY,
    roles: CompanyRole.ADMIN,
  },
  Availability: {
    path: 'availability/*',
    fallbackPath: 'unauthorized',
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.PLANNER,
  },
  Workflows: {
    path: 'management/workflows',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  WorkflowsCreate: {
    path: 'management/workflows/create',
    fallbackPath: 'management/workflows',
    exact: true,
    hqImpersonationOnly: true,
  },
  WorkflowsEdit: {
    path: 'management/workflows/:id/edit',
    fallbackPath: 'management/workflows',
    exact: true,
    hqImpersonationOnly: true,
  },
  Legal: {
    path: 'management/legal',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: 'SPECTATOR',
  },
  Settings: {
    path: 'settings',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  SettingsEdit: {
    path: 'settings/edit',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
  SettingsEditTheme: {
    path: 'settings/theme/edit',
    exact: true,
    policyGroup: PolicyGroups.DEPARTMENT,
    roles: BranchOfficeRole.MANAGER,
  },
} as const;

export { paths };
